import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Download, ScrollText, ShieldCheck } from 'lucide-react'
import {
  AUDIT_ACTIONS,
  AUDIT_CATEGORIES,
  AUDIT_RESULTS,
  auditDiff,
  type AuditCategory,
  type AuditResult,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  Empty,
  FilterRow,
  InfoGrid,
  InfoItem,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  useAuth,
  useToast,
  type BadgeVariant,
  type Column,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import type { Paginated } from '../../lib/rbac-types'

export interface AuditSummary {
  id: number
  createdAt: string
  actor: {
    id: string | null
    type: 'internal' | 'candidate' | 'system' | 'anonymous'
    label: string | null
  }
  action: string
  actionLabel: string
  resourceType: string | null
  resourceId: string | null
  competitionId: string | null
  result: AuditResult
  reason: string | null
  ip: string | null
  hasChanges: boolean
}

export interface AuditDetail extends AuditSummary {
  before: unknown
  after: unknown
  metadata: Record<string, unknown> | null
  userAgent: string | null
  requestId: string | null
  hash: string
  prevHash: string
}

interface Integrity {
  ok: boolean
  total: number
  problems: { id: number; problem: string }[]
}

const PER_PAGE = 50
const RESULT_VARIANT: Record<AuditResult, BadgeVariant> = {
  success: 'success',
  denied: 'danger',
  error: 'warning',
}
const RESOURCE_LABELS: Record<string, string> = {
  user: 'Compte',
  role: 'Profil',
  route: 'Route',
  audit_log: 'Journal',
  competition: 'Concours',
  form: 'Formulaire',
  candidate: 'Candidat',
  duplicate: 'Doublon',
  duplicate_rule: 'Règle de doublon',
  payment: 'Paiement',
  payment_method: 'Moyen de paiement',
  application: 'Candidature',
  correction: 'Correction après dépôt',
}

const dateTime = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'medium',
})

/** Correspondance paramètres d'URL (lisibles, partageables) → filtres de l'API. */
const URL_TO_API = {
  du: 'from',
  au: 'to',
  categorie: 'category',
  action: 'action',
  resultat: 'result',
  acteur: 'actor',
  ressource: 'resourceType',
  id: 'resourceId',
} as const

function isSensitive(action: string) {
  return Boolean((AUDIT_ACTIONS as Record<string, { sensitive?: boolean }>)[action]?.sensitive)
}

/** Journal d'audit (TODO phase 5) — lecture seule, filtres dans l'URL. */
export function AuditPage() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [selected, setSelected] = useState<number | null>(null)

  const filters = useMemo(() => {
    const query: Record<string, string> = {}
    for (const [urlKey, apiKey] of Object.entries(URL_TO_API)) {
      const value = params.get(urlKey)
      if (value) query[apiKey] = value
    }
    return query
  }, [params])
  const page = Number(params.get('page') ?? 1) || 1

  const [actorDraft, setActorDraft] = useState(params.get('acteur') ?? '')

  const update = (changes: Record<string, string>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value)
          else next.delete(key)
        }
        if (!('page' in changes)) next.delete('page')
        return next
      },
      { replace: true }
    )

  const entries = useQuery({
    queryKey: ['admin', 'audit', filters, page],
    queryFn: () =>
      session.api.get<Paginated<AuditSummary>>('/admin/audit', {
        query: { ...filters, page, perPage: PER_PAGE },
      }),
    placeholderData: keepPreviousData,
  })

  const integrity = useMutation({
    mutationFn: () => session.api.get<Integrity>('/admin/audit/integrity'),
  })

  const exportCsv = useMutation({
    mutationFn: () => session.api.download('/admin/audit/export', { query: filters }),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename ?? 'journal-audit.csv'
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      toast.success(t('audit.exported'))
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const category = params.get('categorie') as AuditCategory | null
  const actionOptions = Object.entries(AUDIT_ACTIONS)
    .filter(([, def]) => !category || def.category === category)
    .map(([value, def]) => ({ value, label: def.label }))

  const columns: Column<AuditSummary>[] = [
    {
      key: 'date',
      header: t('audit.date'),
      width: '11rem',
      cell: (e) => (
        <span className="mono" style={{ fontSize: 'var(--text-sm)' }}>
          {dateTime.format(new Date(e.createdAt))}
        </span>
      ),
    },
    { key: 'actor', header: t('audit.actor'), cell: (e) => <ActorCell actor={e.actor} /> },
    {
      key: 'action',
      header: t('audit.action'),
      cell: (e) => (
        <span>
          {isSensitive(e.action) && <span className="sr-only">{t('audit.sensitive')} : </span>}
          <span style={{ fontWeight: isSensitive(e.action) ? 600 : undefined }}>
            {e.actionLabel}
          </span>
          <span className="perm-code">{e.action}</span>
        </span>
      ),
    },
    {
      key: 'resource',
      header: t('audit.resource'),
      cell: (e) =>
        e.resourceType ? (
          <span style={{ fontSize: 'var(--text-sm)' }}>
            {RESOURCE_LABELS[e.resourceType] ?? e.resourceType}
            {e.resourceId && <span className="perm-code">{e.resourceId}</span>}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'result',
      header: t('audit.result'),
      cell: (e) => <Badge variant={RESULT_VARIANT[e.result]}>{AUDIT_RESULTS[e.result]}</Badge>,
    },
    {
      key: 'reason',
      header: t('audit.reason'),
      cell: (e) =>
        e.reason ? (
          <span
            style={{ fontSize: 'var(--text-sm)', display: 'inline-block', maxWidth: '22rem' }}
            title={e.reason}
          >
            {e.reason.length > 80 ? `${e.reason.slice(0, 80)}…` : e.reason}
          </span>
        ) : (
          '—'
        ),
    },
  ]

  const submitActor = (event: FormEvent) => {
    event.preventDefault()
    update({ acteur: actorDraft.trim() })
  }

  return (
    <>
      <PageHeader
        eyebrow={t('audit.eyebrow')}
        title={t('audit.title')}
        description={t('audit.description')}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<ShieldCheck />}
              loading={integrity.isPending}
              onClick={() => integrity.mutate()}
            >
              {t('audit.verify')}
            </Button>
            <Can perm="audit.export">
              <Button
                variant="primary"
                icon={<Download />}
                loading={exportCsv.isPending}
                onClick={() => exportCsv.mutate()}
              >
                {t('audit.export')}
              </Button>
            </Can>
          </>
        }
      />

      {integrity.data && (
        <Alert tone={integrity.data.ok ? 'success' : 'danger'} className="mb-6">
          {integrity.data.ok
            ? t('audit.integrityOk', { total: integrity.data.total.toLocaleString('fr-FR') })
            : t('audit.integrityKo', {
                count: integrity.data.problems.length,
                ids: integrity.data.problems.map((p) => `n° ${p.id} (${p.problem})`).join(', '),
              })}
        </Alert>
      )}
      {integrity.isError && (
        <Alert tone="danger" className="mb-6">
          {errorMessage(integrity.error)}
        </Alert>
      )}

      <Card>
        <FilterRow>
          <Input
            label={t('audit.from')}
            type="date"
            value={params.get('du') ?? ''}
            max={params.get('au') ?? undefined}
            onChange={(e) => update({ du: e.target.value })}
          />
          <Input
            label={t('audit.to')}
            type="date"
            value={params.get('au') ?? ''}
            min={params.get('du') ?? undefined}
            onChange={(e) => update({ au: e.target.value })}
          />
          <Select
            label={t('audit.category')}
            value={category ?? ''}
            onChange={(e) => update({ categorie: e.target.value, action: '' })}
            options={[
              { value: '', label: t('audit.allCategories') },
              ...Object.entries(AUDIT_CATEGORIES).map(([value, label]) => ({ value, label })),
            ]}
          />
          <Select
            label={t('audit.action')}
            value={params.get('action') ?? ''}
            onChange={(e) => update({ action: e.target.value })}
            options={[{ value: '', label: t('audit.allActions') }, ...actionOptions]}
          />
          <Select
            label={t('audit.result')}
            value={params.get('resultat') ?? ''}
            onChange={(e) => update({ resultat: e.target.value })}
            options={[
              { value: '', label: t('audit.allResults') },
              ...Object.entries(AUDIT_RESULTS).map(([value, label]) => ({ value, label })),
            ]}
          />
          <form
            role="search"
            onSubmit={submitActor}
            className="row"
            style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}
          >
            <Input
              label={t('audit.actor')}
              placeholder={t('audit.actorPlaceholder')}
              value={actorDraft}
              onChange={(e) => setActorDraft(e.target.value)}
            />
            <Button type="submit" variant="secondary">
              {t('audit.apply')}
            </Button>
          </form>
        </FilterRow>
        {(params.get('ressource') || Object.keys(filters).length > 0) && (
          <div
            className="row"
            style={{
              gap: 'var(--space-2)',
              padding: '0 var(--space-4) var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            {params.get('ressource') && (
              <Badge variant="indigo">
                {t('audit.resourceFilter', {
                  type: RESOURCE_LABELS[params.get('ressource')!] ?? params.get('ressource'),
                  id: params.get('id') ?? '',
                })}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActorDraft('')
                setParams(new URLSearchParams(), { replace: true })
              }}
            >
              {t('audit.reset')}
            </Button>
          </div>
        )}
        {entries.isError && <Alert tone="danger">{errorMessage(entries.error)}</Alert>}
        <Table
          columns={columns}
          rows={entries.data?.data ?? []}
          rowKey={(e) => String(e.id)}
          loading={entries.isPending}
          caption={t('audit.title')}
          onRowClick={(e) => setSelected(e.id)}
          empty={<Empty icon={<ScrollText aria-hidden="true" />} title={t('audit.empty')} />}
        />
        {entries.data && entries.data.meta.total > PER_PAGE && (
          <Pagination
            page={entries.data.meta.currentPage}
            perPage={entries.data.meta.perPage}
            total={entries.data.meta.total}
            onPageChange={(p) => update({ page: String(p) })}
          />
        )}
      </Card>

      <AuditDetailModal id={selected} onClose={() => setSelected(null)} />
    </>
  )
}

function ActorCell({ actor }: { actor: AuditSummary['actor'] }) {
  const { t } = useTranslation()
  if (actor.type === 'system') return <Badge variant="neutral">{t('audit.system')}</Badge>
  return (
    <span style={{ fontSize: 'var(--text-sm)' }}>
      {actor.label ?? '—'}
      {actor.type === 'anonymous' && (
        <>
          {' '}
          <Badge variant="outline">{t('audit.anonymous')}</Badge>
        </>
      )}
      {actor.type === 'candidate' && (
        <>
          {' '}
          <Badge variant="sahel">Candidat</Badge>
        </>
      )}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/* Détail                                                              */
/* ------------------------------------------------------------------ */

function AuditDetailModal({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const detail = useQuery({
    queryKey: ['admin', 'audit', 'entry', id],
    queryFn: () => session.api.get<AuditDetail>(`/admin/audit/${id}`),
    enabled: id !== null,
    staleTime: Infinity, // une entrée ne change jamais
  })
  const entry = detail.data

  return (
    <Modal
      open={id !== null}
      onClose={onClose}
      size="lg"
      title={t('audit.detailTitle', { id: id ?? '' })}
      description={entry?.actionLabel}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('audit.close')}
        </Button>
      }
    >
      {detail.isError && <Alert tone="danger">{errorMessage(detail.error)}</Alert>}
      {entry && (
        <div className="stack" style={{ gap: 'var(--space-5)' }}>
          <InfoGrid>
            <InfoItem label={t('audit.date')}>
              {dateTime.format(new Date(entry.createdAt))}
            </InfoItem>
            <InfoItem label={t('audit.actor')}>
              <ActorCell actor={entry.actor} />
            </InfoItem>
            <InfoItem label={t('audit.result')}>
              <Badge variant={RESULT_VARIANT[entry.result]}>{AUDIT_RESULTS[entry.result]}</Badge>
            </InfoItem>
            <InfoItem label={t('audit.resource')}>
              {entry.resourceType ? (
                <>
                  {RESOURCE_LABELS[entry.resourceType] ?? entry.resourceType}
                  {entry.resourceId && <span className="perm-code">{entry.resourceId}</span>}
                </>
              ) : (
                '—'
              )}
            </InfoItem>
            <InfoItem label={t('audit.ip')}>{entry.ip ?? '—'}</InfoItem>
            <InfoItem label={t('audit.requestId')}>
              <span className="mono" style={{ fontSize: 'var(--text-xs)' }}>
                {entry.requestId ?? '—'}
              </span>
            </InfoItem>
          </InfoGrid>
          {entry.reason && (
            <Alert tone="info" title={t('audit.reason')}>
              {entry.reason}
            </Alert>
          )}

          <section>
            <h3 className="audit-h">{t('audit.changes')}</h3>
            {entry.before === null && entry.after === null ? (
              <p className="text-muted">{t('audit.noChanges')}</p>
            ) : (
              <DiffTable before={entry.before} after={entry.after} />
            )}
          </section>

          {entry.metadata && (
            <section>
              <h3 className="audit-h">{t('audit.metadata')}</h3>
              <pre className="audit-json">{JSON.stringify(entry.metadata, null, 2)}</pre>
            </section>
          )}

          <section>
            <h3 className="audit-h">{t('audit.integrity')}</h3>
            <dl className="audit-hashes">
              <dt>{t('audit.hash')}</dt>
              <dd className="mono">{entry.hash}</dd>
              <dt>{t('audit.prevHash')}</dt>
              <dd className="mono">{entry.prevHash}</dd>
              {entry.userAgent && (
                <>
                  <dt>{t('audit.userAgent')}</dt>
                  <dd>{entry.userAgent}</dd>
                </>
              )}
            </dl>
          </section>
        </div>
      )}
    </Modal>
  )
}

function formatValue(value: unknown): ReactNode {
  if (value === undefined || value === null || value === '')
    return <span className="text-muted">—</span>
  if (typeof value === 'boolean') return value ? 'oui' : 'non'
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  return <code className="audit-inline">{JSON.stringify(value)}</code>
}

/** Avant/après champ par champ ; pour les listes (ex. permissions) : ajouts et retraits. */
export function DiffTable({ before, after }: { before: unknown; after: unknown }) {
  const { t } = useTranslation()
  const rows = auditDiff(before, after)
  return (
    <div className="tbl-wrap">
      <table className="tbl audit-diff">
        <thead>
          <tr>
            <th scope="col">{t('audit.field')}</th>
            <th scope="col">{t('audit.before')}</th>
            <th scope="col">{t('audit.after')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const listOrNone = (v: unknown) => v === undefined || Array.isArray(v)
            const lists =
              (Array.isArray(row.before) || Array.isArray(row.after)) &&
              listOrNone(row.before) &&
              listOrNone(row.after)
            const b = (row.before as unknown[] | undefined) ?? []
            const a = (row.after as unknown[] | undefined) ?? []
            const added = lists ? a.filter((v) => !b.includes(v)) : []
            const removed = lists ? b.filter((v) => !a.includes(v)) : []
            return (
              <tr key={row.key} data-changed={row.changed || undefined}>
                <th scope="row" className="mono">
                  {row.key}
                </th>
                {lists ? (
                  <td colSpan={2}>
                    <div className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                      {added.map((v) => (
                        <Badge key={`+${String(v)}`} variant="success" title={t('audit.added')}>
                          + {String(v)}
                        </Badge>
                      ))}
                      {removed.map((v) => (
                        <Badge key={`-${String(v)}`} variant="danger" title={t('audit.removed')}>
                          − {String(v)}
                        </Badge>
                      ))}
                      {!added.length && !removed.length && formatValue(row.after)}
                    </div>
                  </td>
                ) : (
                  <>
                    <td>{formatValue(row.before)}</td>
                    <td>{formatValue(row.after)}</td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
